<template>
  <div class="menu-url-picker">
    <div class="menu-url-modes" role="tablist">
      <button type="button" class="menu-url-mode" :class="{ 'is-active': mode === 'route' }" @click="setMode('route')">
        <b-icon icon="signpost-2"></b-icon>
        <span>انتخاب از صفحات سایت</span>
      </button>
      <button type="button" class="menu-url-mode" :class="{ 'is-active': mode === 'direct' }" @click="setMode('direct')">
        <b-icon icon="link-45deg"></b-icon>
        <span>لینک مستقیم</span>
      </button>
    </div>

    <template v-if="mode === 'route'">
      <select class="custom-select" :value="routeName" @change="onRouteChange($event.target.value)">
        <option value="">انتخاب صفحه سایت</option>
        <optgroup label="صفحات ثابت">
          <option v-for="route in staticRoutes" :key="route.name" :value="route.name">
            {{ route.label }} — {{ route.path }}
          </option>
        </optgroup>
        <optgroup label="صفحات با پارامتر">
          <option v-for="route in dynamicRoutes" :key="route.name" :value="route.name">
            {{ route.label }} — {{ route.path }}
          </option>
        </optgroup>
      </select>

      <div v-if="selectedRoute" class="menu-url-params">
        <div v-for="param in selectedRoute.params" :key="param" class="menu-url-param">
          <label class="menus-modal-label">{{ paramLabel(param) }} <span class="text-danger">*</span></label>
          <b-form-input class="text-left" dir="ltr" :value="params[param]" :placeholder="paramPlaceholder(param)"
                        :state="showErrors && !filled(params[param]) ? false : null"
                        @input="setParam(param, $event)"/>

          <div v-if="helperFor(param)" class="menu-url-helper">
            <div class="menu-url-search">
              <b-icon icon="search"></b-icon>
              <input type="text" class="form-control" v-model="helperQuery"
                     :placeholder="`جستجوی ${helperFor(param).title}...`"/>
              <b-spinner v-if="helperLoading" small></b-spinner>
            </div>
            <div class="menu-url-suggestions" v-if="helperOptions.length">
              <button v-for="option in helperOptions" :key="option.value" type="button" class="menu-url-suggestion"
                      :class="{ 'is-selected': option.value === params[param] }"
                      @click="setParam(param, option.value)">
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="menu-url-param">
          <label class="menus-modal-label">پارامترهای اضافه (اختیاری)</label>
          <b-form-input class="text-left" dir="ltr" :value="query" placeholder="key=value&key2=value2"
                        @input="setQuery($event)"/>
          <div v-if="selectedRoute.name === 'Products'" class="menu-url-helper">
            <div class="menu-url-search">
              <b-icon icon="search"></b-icon>
              <input type="text" class="form-control" v-model="helperQuery" placeholder="فیلتر بر اساس دسته‌بندی..."/>
            </div>
            <div class="menu-url-suggestions" v-if="categoryQueryOptions.length">
              <button v-for="option in categoryQueryOptions" :key="option.value" type="button"
                      class="menu-url-suggestion" :class="{ 'is-selected': option.value === query }"
                      @click="setQuery(option.value)">
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>

        <div class="menu-url-preview" :class="{ 'is-invalid': !valid }">
          <b-icon :icon="valid ? 'check2-circle' : 'exclamation-circle'"></b-icon>
          <div class="menu-url-preview-body">
            <span v-if="!complete">مقدار پارامترهای ستاره‌دار را وارد کنید.</span>
            <span v-else-if="tooLong">
              طول مقدار ({{ builtValue.length }} کاراکتر) از حد مجاز {{ maxLength }} کاراکتر بیشتر است؛ مقادیر را کوتاه‌تر کنید.
            </span>
            <code v-if="complete" dir="ltr">{{ builtValue }}</code>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <b-form-input class="text-left" dir="ltr" :value="value" @input="onDirectInput" placeholder="https://example.com/page"/>
      <small class="menus-modal-help">لینک کامل (مثلا https://...) یا مسیر داخلی سایت، مثل /products</small>
    </template>
  </div>
</template>

<script>
import {getAxiosInstance} from "@moeinmdn/mixitweb-front-modules/src/plugin/utils/setInstance.js";
import {findAttributeOnSiteInfo} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/FindAttributeOnSiteInfo";
import {siteRoutes} from "@/plugin/routes/SiteRoutes";

// Persian labels for public storefront routes (route names from SiteRoutes.js)
const ROUTE_LABELS = {
  Home: 'صفحه اصلی',
  Products: 'محصولات',
  Product: 'محصول',
  Categories: 'دسته‌بندی‌ها',
  SingleCategory: 'دسته‌بندی',
  Brands: 'برندها',
  SingleBrand: 'برند',
  Posts: 'وبلاگ',
  SinglePost: 'نوشته وبلاگ',
  PostCategories: 'دسته‌بندی‌های وبلاگ',
  Pages: 'صفحات',
  SinglePage: 'صفحه',
  Courses: 'دوره‌ها',
  SingleCourse: 'دوره',
  SingleLesson: 'درس دوره',
  Search: 'جستجو',
  ShoppingCart: 'سبد خرید',
  UnAuthenticatedShoppingCart: 'سبد خرید مهمان',
  WishListUser: 'علاقه‌مندی‌های کاربر',
  Login: 'ورود',
  Register: 'ثبت‌نام',
  ForgetPassword: 'فراموشی رمز عبور',
}

// internal / flow routes that should not be linked from a menu
const EXCLUDED_ROUTES = [
  'VerifiedCellphone', 'CellphoneResetPassword', 'VerifyEmailRequest', 'EmailResetPassword', 'VerifiedEmail',
  'Delivery', 'Factor', 'Payment', 'verifyTransaction', 'verifyTransactionWithoutAuthentication',
  'SingleChat', 'ProductShare', 'FactorWithSlug', 'Suspend',
]

const PARAM_LABELS = {
  name: 'لینک محصول',
  slug: 'نامک (slug)',
  courseSlug: 'نامک دوره',
  type: 'نوع جستجو',
  words: 'عبارت جستجو',
  user: 'شناسه کاربر',
  id: 'شناسه',
}

// entity search helpers that fill a route param
const PARAM_HELPERS = {
  'Product.name': {title: 'محصول', source: 'products'},
  'SingleCategory.slug': {title: 'دسته‌بندی', source: 'categories'},
  'SingleBrand.slug': {title: 'برند', source: 'brands'},
  'SinglePage.slug': {title: 'صفحه', source: 'pages'},
  'SinglePost.slug': {title: 'نوشته', source: 'posts'},
}

// backend column menu_items.url is varchar(255)
const MAX_URL_LENGTH = 255

let baseListsPromise = null

function siteBase() {
  let link = findAttributeOnSiteInfo('site_link')
  if (Array.isArray(link)) {
    link = link[0]
  }
  return link ? String(link).trim().replace(/\/+$/, '') : ''
}

function listOf(res) {
  const data = res && res.data && res.data.data
  if (Array.isArray(data)) {
    return data
  }
  if (data && Array.isArray(data.data)) {
    return data.data
  }
  return []
}

function flattenCategories(list, depth = 0, result = []) {
  (Array.isArray(list) ? list : []).forEach(item => {
    result.push({...item, _depth: depth})
    flattenCategories(item.children, depth + 1, result)
  })
  return result
}

function safeDecode(text) {
  try {
    return decodeURIComponent(text)
  } catch (e) {
    return text
  }
}

function buildRoutes() {
  const routes = [{name: 'Home', path: '/', params: [], regex: /^\/$/}]
  siteRoutes.forEach(route => {
    if (!route || !route.name || !route.path || EXCLUDED_ROUTES.indexOf(route.name) > -1) {
      return
    }
    const params = []
    const pattern = route.path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/:([A-Za-z0-9_]+)/g, (m, param) => {
      params.push(param)
      return '([^/]+)'
    })
    routes.push({
      name: route.name,
      path: route.path,
      params,
      regex: new RegExp(`^${pattern}/?$`),
      label: ROUTE_LABELS[route.name] || route.name,
    })
  })
  routes[0].label = ROUTE_LABELS.Home
  return routes
}

export default {
  name: "MenuUrlPicker",
  props: {
    value: {
      default: ''
    }
  },
  data() {
    return {
      routes: buildRoutes(),
      maxLength: MAX_URL_LENGTH,
      mode: 'route',
      detected: false,
      routeName: '',
      params: {},
      query: '',
      showErrors: false,
      helperQuery: '',
      helperLoading: false,
      helperTimer: null,
      helperSeq: 0,
      categories: [],
      brands: [],
      remoteOptions: []
    }
  },
  computed: {
    base() {
      return siteBase()
    },
    staticRoutes() {
      return this.routes.filter(route => !route.params.length)
    },
    dynamicRoutes() {
      return this.routes.filter(route => route.params.length)
    },
    selectedRoute() {
      return this.routes.find(route => route.name === this.routeName) || null
    },
    complete() {
      return Boolean(this.selectedRoute) && this.selectedRoute.params.every(param => this.filled(this.params[param]))
    },
    queryObject() {
      const text = String(this.query || '').trim().replace(/^\?+/, '')
      const result = {}
      if (!text) {
        return result
      }
      new URLSearchParams(text).forEach((value, key) => {
        if (key) {
          result[key] = value
        }
      })
      return result
    },
    builtValue() {
      // vue-router location object, saved as JSON string (storefront m-a renders it as router-link)
      if (!this.selectedRoute) {
        return ''
      }
      const location = {name: this.selectedRoute.name}
      if (this.selectedRoute.params.length) {
        const params = {}
        this.selectedRoute.params.forEach(param => {
          params[param] = String(this.params[param] || '').trim()
        })
        location.params = params
      }
      if (Object.keys(this.queryObject).length) {
        location.query = this.queryObject
      }
      return JSON.stringify(location)
    },
    tooLong() {
      return this.builtValue.length > MAX_URL_LENGTH
    },
    valid() {
      return this.complete && !this.tooLong
    },
    activeHelper() {
      if (!this.selectedRoute) {
        return null
      }
      const param = this.selectedRoute.params.find(item => PARAM_HELPERS[`${this.selectedRoute.name}.${item}`])
      return param ? PARAM_HELPERS[`${this.selectedRoute.name}.${param}`] : null
    },
    helperOptions() {
      const helper = this.activeHelper
      if (!helper) {
        return []
      }
      const q = this.helperQuery.trim().toLowerCase()
      let options = []
      if (helper.source === 'categories') {
        options = this.categories.map(item => ({label: item.name, value: item.slug}))
      } else if (helper.source === 'brands') {
        options = this.brands.map(item => ({label: item.name, value: item.slug}))
      } else {
        return this.remoteOptions
      }
      return options.filter(item => item.value && (!q || String(item.label).toLowerCase().indexOf(q) > -1))
          .slice(0, 12)
    },
    categoryQueryOptions() {
      const q = this.helperQuery.trim().toLowerCase()
      return this.categories
          .filter(item => !q || String(item.name).toLowerCase().indexOf(q) > -1)
          .slice(0, 12)
          .map(item => ({label: item.name, value: `category_column=id&category=${item.id}`}))
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (!this.detected && value) {
          this.detected = true
          this.detect(value)
        }
      }
    },
    helperQuery() {
      clearTimeout(this.helperTimer)
      this.helperTimer = setTimeout(this.searchRemote, 350)
    }
  },
  methods: {
    filled(value) {
      return value !== undefined && value !== null && String(value).trim() !== ''
    },
    paramLabel(param) {
      return PARAM_LABELS[param] || param
    },
    paramPlaceholder(param) {
      return param === 'name' ? 'مثلا: product-link' : param === 'slug' ? 'مثلا: about-us' : param
    },
    helperFor(param) {
      return this.selectedRoute ? PARAM_HELPERS[`${this.selectedRoute.name}.${param}`] || null : null
    },
    relativePath(value) {
      const text = String(value || '').trim()
      if (this.base && text.indexOf(this.base) === 0) {
        return text.slice(this.base.length) || '/'
      }
      if (text[0] === '/') {
        return text
      }
      return null
    },
    detect(value) {
      const text = String(value || '').trim()
      if (text[0] === '{') {
        let location = null
        try {
          location = JSON.parse(text)
        } catch (e) {
          location = null
        }
        const route = location && this.routes.find(item => item.name === location.name)
        if (route) {
          const params = {}
          route.params.forEach(param => {
            const raw = location.params && location.params[param]
            params[param] = raw === undefined || raw === null ? '' : String(raw)
          })
          this.mode = 'route'
          this.routeName = route.name
          this.params = params
          this.query = location.query && typeof location.query === 'object'
              ? new URLSearchParams(location.query).toString()
              : ''
          this.onRouteSelected()
          return
        }
        this.mode = 'direct'
        return
      }
      const relative = this.relativePath(value)
      if (relative !== null) {
        const [pathPart, ...rest] = relative.split('?')
        const path = pathPart || '/'
        const ordered = [...this.staticRoutes, ...this.dynamicRoutes]
        for (const route of ordered) {
          const match = path.match(route.regex)
          if (match) {
            const params = {}
            route.params.forEach((param, index) => {
              params[param] = safeDecode(match[index + 1])
            })
            this.mode = 'route'
            this.routeName = route.name
            this.params = params
            this.query = safeDecode(rest.join('?'))
            this.onRouteSelected()
            return
          }
        }
      }
      this.mode = 'direct'
    },
    setMode(mode) {
      this.detected = true
      this.mode = mode
      if (mode === 'route' && this.selectedRoute) {
        this.emitBuilt()
      }
    },
    onRouteChange(name) {
      this.detected = true
      this.routeName = name
      const params = {}
      if (this.selectedRoute) {
        this.selectedRoute.params.forEach(param => {
          params[param] = ''
        })
      }
      this.params = params
      this.query = ''
      this.helperQuery = ''
      this.remoteOptions = []
      this.showErrors = false
      this.onRouteSelected()
      this.emitBuilt()
    },
    onRouteSelected() {
      if (!this.selectedRoute) {
        return
      }
      const needsBase = this.selectedRoute.name === 'Products'
          || (this.activeHelper && ['categories', 'brands'].indexOf(this.activeHelper.source) > -1)
      if (needsBase) {
        this.loadBaseLists()
      }
      if (this.activeHelper && ['products', 'pages', 'posts'].indexOf(this.activeHelper.source) > -1) {
        this.searchRemote()
      }
    },
    setParam(param, value) {
      this.detected = true
      this.params = {...this.params, [param]: value}
      this.showErrors = true
      this.emitBuilt()
    },
    setQuery(value) {
      this.detected = true
      this.query = value
      this.emitBuilt()
    },
    emitBuilt() {
      // incomplete params / too long value emit empty so the form's required validation blocks saving
      this.$emit('input', this.valid ? this.builtValue : '')
    },
    onDirectInput(value) {
      this.detected = true
      this.$emit('input', value)
    },
    loadBaseLists() {
      if (!baseListsPromise) {
        const axios = getAxiosInstance()
        baseListsPromise = Promise.all([
          axios.get('category').then(listOf).catch(() => []),
          axios.get('brand/without/pagination').then(listOf).catch(() => []),
        ]).then(([categories, brands]) => ({categories: flattenCategories(categories), brands}))
      }
      return baseListsPromise.then(lists => {
        this.categories = lists.categories
        this.brands = lists.brands
      }).catch(() => {
        baseListsPromise = null
      })
    },
    searchRemote() {
      const helper = this.activeHelper
      if (!helper || ['products', 'pages', 'posts'].indexOf(helper.source) === -1) {
        return
      }
      const axios = getAxiosInstance()
      const search = this.helperQuery.trim()
      const params = search ? {search} : {}
      const seq = ++this.helperSeq
      let request
      if (helper.source === 'products') {
        request = axios.get('product', {params}).then(listOf)
            .then(list => list.filter(item => item && item.raw_link).map(item => ({label: item.name, value: item.raw_link})))
      } else {
        const contentType = helper.source === 'pages' ? 'page' : 'post'
        request = axios.get('content', {params: {...params, content_type: contentType}}).then(listOf)
            .then(list => list.filter(item => item && item.slug).map(item => ({label: item.title, value: item.slug})))
      }
      this.helperLoading = true
      request.catch(() => []).then(options => {
        if (seq === this.helperSeq) {
          this.remoteOptions = options.slice(0, 12)
        }
      }).finally(() => {
        if (seq === this.helperSeq) {
          this.helperLoading = false
        }
      })
    }
  },
  beforeDestroy() {
    clearTimeout(this.helperTimer)
  }
}
</script>

<style scoped>
.menu-url-modes {
  display: flex;
  gap: 4px;
  background: #f4f4f4;
  border-radius: 999px;
  padding: 4px;
  margin-bottom: 10px;
}

.menu-url-mode {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  background: transparent;
  color: #555;
  border-radius: 999px;
  padding: 7px 10px;
  font-size: 12.5px;
  font-weight: 800;
  cursor: pointer;
}

.menu-url-mode:focus {
  outline: none;
}

.menu-url-mode.is-active {
  background: #1e1e1e;
  color: #fff;
}

.menu-url-params {
  margin-top: 10px;
  border: 1px solid #eee;
  border-radius: 14px;
  background: #fcfcfc;
  padding: 12px;
}

.menu-url-param + .menu-url-param {
  margin-top: 12px;
}

.menu-url-helper {
  margin-top: 6px;
}

.menu-url-search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
}

.menu-url-search > svg:first-child {
  position: absolute;
  right: 12px;
  color: #999;
  pointer-events: none;
}

.menu-url-search .form-control {
  padding-right: 34px;
  min-height: 36px;
  font-size: 12.5px;
}

.menu-url-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
  max-height: 120px;
  overflow-y: auto;
}

.menu-url-suggestion {
  border: 1px solid #e6e6e6;
  background: #fff;
  color: #333;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.menu-url-suggestion:hover,
.menu-url-suggestion:focus {
  border-color: #eb3d60;
  color: #eb3d60;
  outline: none;
}

.menu-url-suggestion.is-selected {
  background: #eb3d60;
  border-color: #eb3d60;
  color: #fff;
}

.menu-url-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  padding: 8px 10px;
  border-radius: 12px;
  background: #e6f6ec;
  color: #1e7a3c;
  font-size: 12px;
  font-weight: 700;
  word-break: break-all;
}

.menu-url-preview.is-invalid {
  background: #fff4e0;
  color: #b26a00;
}

.menu-url-preview svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.menu-url-preview {
  align-items: flex-start;
}

.menu-url-preview-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.menu-url-preview code {
  color: inherit;
  font-size: 11.5px;
  word-break: break-all;
  text-align: left;
}

@media (max-width: 575px) {
  .menu-url-mode span {
    font-size: 11.5px;
  }
}
</style>
